<template>
  <q-card class="widget-card">
    <div class="widget-card__header">
      <div class="flex items-center no-wrap">
        <q-icon
          name="sym_r_sell"
          size="22px"
          class="q-mr-xs"
          style="margin-bottom: -1px"
        />
        <div class="widget-card__title">Deals Distribution</div>
      </div>
      <div class="widget-card__subtitle">
        How deals are spread across the market.
      </div>
    </div>
    <div class="row q-col-gutter-sm q-px-md q-pb-sm">
      <div class="col-12">
        <q-select
          v-model="selectedCondition"
          :options="conditionOptions"
          color="secondary"
          dense
          @update:model-value="fetchDistribution"
        />
      </div>
    </div>
    <div class="widget-card__content">
      <div v-if="loading" class="q-pb-md">
        <q-skeleton type="text" height="40px" />
        <q-skeleton type="text" height="40px" />
        <q-skeleton type="text" height="40px" />
        <q-skeleton type="text" height="40px" />
        <q-skeleton type="text" height="40px" />
      </div>
      <div v-else>
        <div class="component-bars">
          <div
            v-for="(segment, index) in distribution"
            :key="index"
            class="component-bar-item cursor-pointer"
            @click="
              () => {
                searchByDealScore(segment.key);
              }
            "
          >
            <div class="component-bar-row">
              <div class="component-bar-badge">
                <ScoreBadge :score="convertScorePhraseToScore(segment.key)" />
              </div>
              <div class="component-bar-track">
                <div
                  class="component-bar-fill"
                  :style="{
                    width: segment.percentage + '%',
                    background: convertScoreToColor(
                      convertScorePhraseToScore(segment.key)
                    )
                  }"
                />
              </div>
              <div class="component-bar-stats">
                <span class="text-weight-bold">{{ segment.percentage }}%</span>
                <span class="count-text"
                  >({{ formatNumber(segment.count, true, 0) }})</span
                >
              </div>
            </div>
            <q-icon
              name="sym_r_chevron_right"
              size="22px"
              color="text-light"
              class="component-bar-chevron"
            />
          </div>
        </div>

        <div class="insight-box q-mt-md">
          <q-icon name="sym_r_campaign" size="24px" class="q-mr-xs" />
          <div class="insight-box__text">
            <span class="text-weight-bold">{{ bestValuePercentage }}%</span> of
            vehicles are rated Good or better, a great time to shop.
          </div>
        </div>
      </div>
    </div>
  </q-card>
</template>

<script>
import axios from "axios";
import mixins from "@/mixins";
import ScoreBadge from "@/components/UI/ScoreBadge.vue";

export default {
  name: "DealScoreDistributionWidget",
  components: {
    ScoreBadge
  },
  mixins: [mixins],
  data() {
    return {
      loading: true,
      distribution: [],
      totalCount: 0,
      selectedCondition: { label: "All Vehicles", value: null },
      conditionOptions: [
        { label: "All Vehicles", value: null },
        { label: "New Vehicles", value: "New" },
        { label: "Used Vehicles", value: "Used" }
      ]
    };
  },
  computed: {
    bestValuePercentage() {
      const goodOrBetter = this.distribution
        .filter(d => ["excellent", "great", "good"].includes(d.key))
        .reduce((sum, d) => sum + d.percentage, 0);
      return Math.round(goodOrBetter);
    }
  },
  mounted() {
    this.fetchDistribution();
  },
  methods: {
    async fetchDistribution() {
      try {
        this.loading = true;
        const filters = [];

        if (this.selectedCondition?.value) {
          filters.push({ condition: { in: [this.selectedCondition.value] } });
        }

        const response = await axios.post(
          "https://api-v2.jumpfeed.com/network/publisher/products/vehicles",
          {
            filters: filters,
            pagination: { page: 1, per_page: 1 },
            options: {
              location_search: {
                postal_code: this.$store.state.search.location.postalCode || "",
                radius: this.$store.state.search.location.radius || 100,
                include_distance_field: true
              },
              faceting: {
                fields: { deal_score_phrase: [] },
                size: 10
              }
            }
          }
        );

        const facets = response.data.metadata?.facets?.deal_score_phrase || [];
        this.totalCount = facets.reduce((sum, f) => sum + f.doc_count, 0);

        const scoreOrder = ["excellent", "great", "good", "fair", "poor"];

        this.distribution = scoreOrder
          .map(key => {
            const facet = facets.find(f => f.key.toLowerCase() === key);
            if (!facet) return null;

            return {
              key,
              count: facet.doc_count,
              percentage: Math.round((facet.doc_count / this.totalCount) * 100)
            };
          })
          .filter(Boolean);
      } catch (error) {
        console.error("Failed to fetch distribution:", error);
      } finally {
        this.loading = false;
      }
    },
    searchByDealScore(dealScore) {
      this.$store.commit("search/SET_SEARCH_FILTERS", [
        { deal_score_phrase: { in: [dealScore] } }
      ]);
      this.$store.commit("search/SET_LOADING", true);
      this.$router.push({ name: "search" });
    }
  }
};
</script>

<style scoped lang="scss">
.component-bars {
  display: flex;
  flex-direction: column;
}

.component-bar-item {
  display: flex;
  align-items: center;
  gap: $space-xs;
  padding: $space-sm;
  background: color(background, lighter);
  border-radius: 6px;
  transition: all 0.2s ease;
  margin-left: -8px;

  &:hover {
    background: color(background, very-light);
    transform: translateX(2px);
    padding-left: 10px;

    .component-bar-track {
      background: color(background, light) !important;
    }
  }

  .component-bar-row {
    display: flex;
    align-items: center;
    gap: $space-md;
    font-size: 14px;
    flex: 1;
  }

  .component-bar-badge {
    flex-shrink: 0;
  }

  .component-bar-track {
    flex: 1;
    height: 8px;
    background: color(background, very-light);
    border-radius: 4px;
    overflow: hidden;
    transition: all 0.2s ease;

    .component-bar-fill {
      height: 100%;
      border-radius: 4px;
      transition: width 0.3s ease;
    }
  }

  .component-bar-stats {
    display: flex;
    align-items: center;
    gap: $space-xs;
    flex-shrink: 0;
    justify-content: flex-start;
    font-size: 0.95rem;

    .count-text {
      font-size: 0.85rem;
      color: color(text, light);
    }
  }

  .component-bar-chevron {
    flex-shrink: 0;
    margin-right: -8px;
  }
}
</style>
